import { UseFormRegisterReturn, useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Member } from "../../../service/model/member.model";
import { Box, Button, Dialog, DialogTitle, Grid } from "@mui/material";
import FormField from "../../../components/form-controls/form-field";
import theme from "../../../app/app-theme";
import useMemberships from "../../../hooks/use-memberships";
import { useEffect, useState } from "react";
import { addDays } from "date-fns";
import ProfileImageDropzone from "./profile-image-dropzone";
import FormCloseButton from "../../../components/form-controls/form-close-button";
import { FormTextField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";
import { FormSelectField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-select-field";

interface MemberFormProps {
    readonly member?: Member
    readonly editable: boolean
    readonly onClose: () => void
    readonly onSubmit: (data: Member, image?: File | null) => void
}

const style = {
    // position: 'absolute' as 'absolute',
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    // width: '100%',
    // bgcolor: 'background.paper',
    // border: '2px solid #000',
    // boxShadow: 24,
    p: 2,
};

const validationSchema = yup.object().shape({
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    email: yup.string().email("Not a valid email"),
    gender: yup.string().required("Gender is required"),
    dateOfBirth: yup.string(),
    contact: yup.string().required("Contact is required").matches(/^\+947\d{8}$/, 'Contact number follow format +947XXXXXXXX (e.g. +94771399297)').length(12),
    address: yup.string(),
    membershipStatus: yup.string().required("Membership Status is required"),
    attendanceStatus: yup.string().required("Attendance Status is required"),
    dueAmount: yup.number(),
    membershipId: yup.number().transform((value) => Number.isNaN(value) ? null : value).required("Membership is required"),
    joinedDate: yup.string(),
    expiryDate: yup.string(),
    lastAttendedDate: yup.string(),
});

export type FormValues = yup.InferType<typeof validationSchema>;

const MemberForm: React.FC<MemberFormProps> = ({ member, editable, onClose, onSubmit }) => {
    const [memberships] = useMemberships();
    const [profileImage,  setProfileImage] = useState<File | null>(null);

    const { register, watch, setValue, handleSubmit, formState: { errors } } = useForm<FormValues>({
        resolver: yupResolver(validationSchema)
    });

    const selectedMembership = watch("membershipId");

    useEffect(() => {
        const membership = memberships.find(membership => membership.id === selectedMembership)
        membership && setValue("expiryDate", addDays(new Date(), membership.durationDays).toISOString().slice(0, 10),);
    }, [selectedMembership]);

    const onSubmitHandler = (data: any) => {
        const updatedMember = {
            ...member,
            ...data
        }
        onSubmit(updatedMember as Member, profileImage)
    };

    const getRegister = (name: keyof FormValues, validation?: any): UseFormRegisterReturn => {
        return register(name, validation);
    };

    return (
        <Dialog open={true} onClose={onClose} sx={style} fullWidth maxWidth='md'>
            <DialogTitle sx={{ paddingBottom: 0 }}>Member Details</DialogTitle>

            <Box sx={style}>
                <>
                    <FormCloseButton onClose={onClose} />
                    <ProfileImageDropzone onFileChange={(file) => setProfileImage(file)} file={member?.profileImageKey} />
                    <form onSubmit={handleSubmit(onSubmitHandler)}>
                        <Grid container direction="row" className="mt-0 my-0" justifyContent='space-between'>
                            <FormField>
                                <FormTextField
                                    id="firstName"
                                    label="First Name"
                                    name="firstName"
                                    defaultValue={member?.firstName}
                                    errorMessage={errors.firstName?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="lastName"
                                    label="Last Name"
                                    name="lastName"
                                    defaultValue={member?.lastName}
                                    errorMessage={errors.lastName?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="email"
                                    label="Email"
                                    name="email"
                                    defaultValue={member?.email}
                                    errorMessage={errors.email?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormSelectField
                                    id={"gender"}
                                    label="Gender"
                                    name="gender"
                                    defaultValue={member?.gender}
                                    errorMessage={errors.gender?.message}
                                    options={["Male", "Female"].map(item => { return { label: item, value: item } })}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="dateOfBirth"
                                    label="Birth Day"
                                    type="date"
                                    name="dateOfBirth"
                                    defaultValue={member?.dateOfBirth ?? ''}
                                    errorMessage={errors.dateOfBirth?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="contact"
                                    label="Contact"
                                    name="contact"
                                    defaultValue={member?.contact ?? "+94"}
                                    errorMessage={errors.contact?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="address"
                                    label="Address"
                                    name="address"
                                    defaultValue={member?.address}
                                    errorMessage={errors.address?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormSelectField
                                    id={"membershipStatus"}
                                    label="Membership Status"
                                    name="membershipStatus"
                                    defaultValue={member?.membershipStatus ?? "Active"}
                                    errorMessage={errors.membershipStatus?.message}
                                    options={["Active", "Expired"].map(item => { return { label: item, value: item } })}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormSelectField
                                    id={"attendanceStatus"}
                                    label="Attendance Status"
                                    name="attendanceStatus"
                                    defaultValue={member?.attendanceStatus ?? "Active"}
                                    errorMessage={errors.attendanceStatus?.message}
                                    options={["Active", "Inactive"].map(item => { return { label: item, value: item } })}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="dueAmount"
                                    label="Due Amount"
                                    type="number"
                                    name="dueAmount"
                                    defaultValue={member?.dueAmount ?? 0}
                                    errorMessage={errors.dueAmount?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormSelectField
                                    id={"membership"}
                                    label="Membership"
                                    name="membershipId"
                                    defaultValue={member?.membershipId}
                                    errorMessage={errors.membershipId?.message}
                                    options={memberships.map(membership => { return { label: membership.name, value: membership.id } })}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="joinedDate"
                                    label="Joined Date"
                                    name="joinedDate"
                                    type="date"
                                    defaultValue={member?.joinedDate ?? new Date().toISOString().slice(0, 10)}
                                    errorMessage={errors.joinedDate?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="expiryDate"
                                    label="Expiry Date"
                                    name="expiryDate"
                                    type="date"
                                    defaultValue={member?.expiryDate ?? ''}
                                    errorMessage={errors.expiryDate?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="lastAttendedDate"
                                    label="Last Attended Date"
                                    name="lastAttendedDate"
                                    type="date"
                                    defaultValue={member?.lastAttendedDate ?? ''}
                                    errorMessage={errors.lastAttendedDate?.message}
                                    register={register}
                                />
                            </FormField>
                            {/* {member &&
                                <>
                                    <FormField>
                                        <TextField
                                            id="id"
                                            label="Member ID"
                                            value={member.id}
                                            disabled
                                        />
                                    </FormField>
                                    <FormField>
                                        <TextField
                                            id="pin"
                                            label="PIN"
                                            value={member.pin}
                                            disabled
                                        />
                                    </FormField>
                                </>
                            } */}
                        </Grid>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained">Submit</Button>
                        </Grid>
                    </form>
                </>
            </Box>
        </Dialog>
    );
}

export default MemberForm;